import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Breadcrumbs, Button, Header, LoginModal, Title } from "../../../components";
import { fetchGetHall } from "../../../redux/halls/asyncActions";
import { hallsSelector } from "../../../redux/halls/selectors";
import { setDate, setLastSelectedHall, resetSelectedHalls } from "../../../redux/halls/slice";
import { ISelectedHall } from "../../../redux/halls/types";
import { useAppDispatch } from "../../../redux/store";
import { setCartItems } from "../../../redux/cart/slice";
import { subscriptionsSelector } from "../../../redux/subscriptions/selectors";
import {
  getDateFromStorage,
  getLastSelectedHallFromStorage,
} from "../../../utils/storage/halls";
import { HallTitle, Navigation, Price, TimesTable } from "../components";
import { createReserveArena } from "../../../redux/rent/bitrixArenas";
import store from "../../../redux/store";
import { sendFrontendLog } from "../../../redux/frontendLog/asyncActions";
import { isAuthenticatedSelector } from "../../../redux/profile/authAndReg/selectors";
import { setShowWindow } from "../../../redux/profile/authAndReg/slice";
import { WindowNames } from "../../../resources/constants";

import styles from "./HallsTime.module.scss";

import { HallsTimePropsType } from "./types";

export const HallsTime: React.FC<HallsTimePropsType> = ({ title }) => {
  const { date, lastSelectedHall, selectedHalls } = useSelector(hallsSelector);
  const { selectedSubscr } = useSelector(subscriptionsSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginModalOpen, setIsLoginModalOpen] = React.useState(false);
  const [pendingReservation, setPendingReservation] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  React.useEffect(() => {
    const date = getDateFromStorage();
    const lastSelectedHall = getLastSelectedHallFromStorage();

    if (date) dispatch(setDate(date));
    if (lastSelectedHall) dispatch(setLastSelectedHall(lastSelectedHall));
  }, []);

  React.useEffect(() => {
    if (date && lastSelectedHall) {
      const hallId = lastSelectedHall.id;
      dispatch(fetchGetHall({ hallId, date }));
      dispatch(resetSelectedHalls());
    }
  }, [date, lastSelectedHall]);

  const hasSelectedHalls = Object.keys(selectedHalls).length > 0;

  const handleAddToCart = React.useCallback(async () => {
    if (!hasSelectedHalls || !lastSelectedHall) return;

    if (!isAuthenticated) {
      dispatch(setShowWindow(WindowNames.AUTH));
      setIsLoginModalOpen(true);
      setPendingReservation(true);
      return;
    }

    try {
      const state = store.getState();
      const partnerId = (state as any).user?.partnerId;

      if (!partnerId) {
        navigate("/cart");
        return;
      }

      const selectedSlots = Object.values(selectedHalls);
      const courtId = lastSelectedHall.courtId || lastSelectedHall.id.toString();
      
      const reserves = selectedSlots
        .filter(slot => slot.startTime && slot.finishTime)
        .map(slot => {
          return {
            courtId: courtId,
            startTime: slot.startTime!,
            finishTime: slot.finishTime!,
          };
        });

      if (reserves.length > 0) {
        const reserveData = {
          partnerId: partnerId,
          newReserve: reserves,
          cancelReserve: [],
        };

        await createReserveArena(reserveData);

        dispatch(sendFrontendLog({
          type: "user_action",
          data: {
            message: "Hall reservation created",
            component: "HallsTime",
            timestamp: new Date().toISOString(),
            url: window.location.pathname,
            userAgent: navigator.userAgent,
            reservationData: {
              courtId: courtId,
              date: date,
              reservesCount: reserves.length,
              hallTitle: lastSelectedHall.title,
            },
          },
        }));
      }

      const enrichedSeats: { [key: string]: ISelectedHall } = {};
      Object.keys(selectedHalls).forEach(key => {
        enrichedSeats[key] = {
          ...selectedHalls[key],
          courtId: courtId,
        };
      });

      const totalCartItems = {
        seats: enrichedSeats,
        subscriptions: { ...selectedSubscr },
      };
      dispatch(setCartItems(totalCartItems));

      navigate("/cart");
    } catch (error) {
      dispatch(sendFrontendLog({
        type: "error",
        data: {
          message: "Error creating hall reservation",
          component: "HallsTime",
          timestamp: new Date().toISOString(),
          url: window.location.pathname,
          userAgent: navigator.userAgent,
          error: error instanceof Error ? error.message : String(error),
          reservationData: {
            courtId: lastSelectedHall?.courtId || lastSelectedHall?.id.toString(),
            date: date,
            selectedSlotsCount: Object.keys(selectedHalls).length,
          },
        },
      }));
      
      navigate("/cart");
    }
  }, [hasSelectedHalls, lastSelectedHall, isAuthenticated, selectedHalls, selectedSubscr, date, dispatch, navigate]);

  React.useEffect(() => {
    if (isAuthenticated && pendingReservation && isLoginModalOpen) {
      setIsLoginModalOpen(false);
      setPendingReservation(false);
      setTimeout(() => {
        handleAddToCart();
      }, 100);
    }
  }, [isAuthenticated, pendingReservation, isLoginModalOpen, handleAddToCart]);

  return (
    <>
      <Header title={title} />
      <main>
        <div className="container">
          <Breadcrumbs />
        </div>
        <section className="container container--textAlignCenter">
          <Title>{title}</Title>
          <HallTitle />
          <Navigation />
          <TimesTable />
          {hasSelectedHalls && (
            <Button newStyles={styles.button} onClickFunc={handleAddToCart}>
              В корзину
            </Button>
          )}
        </section>
      </main>
      {isLoginModalOpen && (
        <LoginModal isOpen={isLoginModalOpen} setIsOpen={setIsLoginModalOpen} />
      )}
    </>
  );
};
